import type { ScoreDistributionItem } from "@foodpilot/foods";
import type { DimensionValue, MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";

export type ScoreBreakdownMode = "lifecycle" | "subscores" | "impacts";
type FoodsIconName = NonNullable<ScoreDistributionItem["icon"]>;

export type ScoreBreakdownIconTone =
    | "dataviz-1"
    | "dataviz-2"
    | "dataviz-3"
    | "dataviz-4"
    | "dataviz-5"
    | "dataviz-6"
    | "dataviz-7"
    | "dataviz-9"
    | "dataviz-13"
    | "green";

export type ScoreBreakdownItem = {
    id: string;
    label: string;
    description?: string;
    value: number | null;
    unit: string;
    children: ScoreBreakdownItem[];
    icon?: FoodsIconName;
    iconTone?: ScoreBreakdownIconTone;
};

export type StageDefinition = {
    id: string;
    label: string;
    icon?: FoodsIconName;
    iconTone?: ScoreBreakdownIconTone;
    useDimensionLabel?: boolean;
};

type GetDimensionValueLocalizedLabel = (dimension: string, key: string | number) => string | null;

export type SubscoreDefinition = {
    id: number;
    title: string;
    label?: string | null;
    code?: string | null;
    description?: string | null;
    unit?: { abbreviation: string } | null;
};

const getNegativeScorePresentation = (value: number | null) =>
    value !== null && value < 0 ? { icon: "carbonNeutrality" as const, iconTone: "green" as const } : {};

export const getNumericalValue = (score: MainScore | DimensionValue | undefined): number | null =>
    OlapScoresService.normalizeNumericalScore(score?.score.value, null);

const getNestedDimensionItems = (
    dimensions: DimensionValue["dimensions"],
    unit: string,
    getDimensionValueLocalizedLabel?: GetDimensionValueLocalizedLabel,
) => {
    const [dimension] = dimensions;
    if (dimension === undefined) return [];

    return dimension.values.flatMap((value): ScoreBreakdownItem[] => {
        const numericalValue = getNumericalValue(value);
        if (numericalValue === null) return [];

        return [
            {
                id: `${dimension.label}-${value.id}`,
                label: getDimensionValueLocalizedLabel?.(dimension.label, value.id) ?? value.label,
                value: numericalValue,
                unit,
                children: getNestedDimensionItems(value.dimensions, unit, getDimensionValueLocalizedLabel),
                ...getNegativeScorePresentation(numericalValue),
            },
        ];
    });
};

export const getLifecycleItems = (
    mainScore: MainScore | undefined,
    stages: StageDefinition[],
    getDimensionValueLocalizedLabel?: GetDimensionValueLocalizedLabel,
): ScoreBreakdownItem[] => {
    const components = OlapScoresService.getDimension("components", mainScore?.dimensions);
    const total = getNumericalValue(mainScore);
    const absoluteUnit = mainScore?.unit ?? "";

    if (total === null) return [];

    const resolvedStages = stages.map((stage) => {
        const dimensionValue = OlapScoresService.getDimensionValue(stage.id, components);

        return { absoluteValue: getNumericalValue(dimensionValue), dimensionValue, stage };
    });

    if (resolvedStages.every(({ absoluteValue }) => absoluteValue === null)) return [];

    return resolvedStages.map(({ absoluteValue, dimensionValue, stage }): ScoreBreakdownItem => {
        const value = absoluteValue ?? 0;

        return {
            id: stage.id,
            label:
                stage.useDimensionLabel ?
                    (getDimensionValueLocalizedLabel?.("components", stage.id) ?? dimensionValue?.label ?? stage.label)
                :   stage.label,
            value,
            unit: absoluteUnit,
            icon: stage.icon,
            iconTone: stage.iconTone,
            children: getNestedDimensionItems(
                dimensionValue?.dimensions ?? [],
                absoluteUnit,
                getDimensionValueLocalizedLabel,
            ),
        };
    });
};

const getSubscorePresentation = (id: string) => {
    switch (id) {
        case "climate":
        case "climate-change":
        case "climate_change":
            return { icon: "carbon" as const, iconTone: "dataviz-13" as const };
        case "biodiversity":
            return { icon: "biodiversity" as const, iconTone: "dataviz-1" as const };
        case "environmental-health":
        case "environmental_health":
            return { icon: "environment" as const, iconTone: "dataviz-3" as const };
        case "resource":
        case "resources":
            return { icon: "trajectory" as const, iconTone: "dataviz-9" as const };
        case "ecosystem-services":
        case "ecosystem_services":
            return { icon: "carbonNeutrality" as const, iconTone: "green" as const };
        default:
            return {};
    }
};

export const getSubscoreItems = (
    mainScore: MainScore | undefined,
    definitions: readonly SubscoreDefinition[] = [],
): ScoreBreakdownItem[] => {
    const definitionsById = new Map(definitions.map((definition) => [definition.id.toString(), definition]));

    return Object.entries(mainScore?.subscores ?? {}).flatMap(([id, score]) => {
        if (score.type === "specificity-rate" || score.type === "impact") return [];

        const definition = definitionsById.get(id);
        if (definition === undefined) return [];

        const description = definition?.description?.trim();
        const presentationId = definition?.code?.trim() || id;
        const numericalValue = getNumericalValue(score);
        if (numericalValue === null) return [];

        return [
            {
                id,
                label: definition.title,
                ...(description ? { description } : {}),
                value: numericalValue,
                unit: definition.unit?.abbreviation ?? score.unit,
                children: [],
                ...getSubscorePresentation(presentationId),
            },
        ];
    });
};

export const getImpactItems = (
    mainScore: MainScore | undefined,
    definitions: readonly SubscoreDefinition[] = [],
): ScoreBreakdownItem[] => {
    const definitionsById = new Map(definitions.map((definition) => [definition.id.toString(), definition]));

    return Object.entries(mainScore?.subscores ?? {}).flatMap(([impactId, impact]): ScoreBreakdownItem[] => {
        if (impact.type !== "impact") return [];

        const definition = definitionsById.get(impactId);
        if (definition === undefined) return [];

        const numericalValue = getNumericalValue(impact);
        if (numericalValue === null) return [];
        const description = definition.description?.trim();

        return [
            {
                id: impactId,
                label: definition.title,
                ...(description ? { description } : {}),
                value: numericalValue,
                unit: definition.unit?.abbreviation ?? impact.unit,
                children: [],
                ...getNegativeScorePresentation(numericalValue),
            },
        ];
    });
};

export const getMaxMagnitude = (items: ScoreBreakdownItem[]) =>
    items.reduce((maximum, item) => Math.max(maximum, Math.abs(item.value ?? 0)), 0);
